import React, { useState, useEffect, useMemo } from 'react';
import { Button, Tag, Tooltip, Popconfirm, message, Space, Table, Card } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ArrowUpOutlined, ArrowDownOutlined, QuestionCircleFilled } from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import InventoryAttributeDefinitionConceptModal from '@shared/inventory_attribute_definitions/InventoryAttributeDefinitionConceptModal';
import { useTranslation } from 'react-i18next';
import { inventoryAttributeDefinitionService } from '@services/admin/inventoryAttributeDefinitionService';
import DefinitionForm from './Form';

export default function List() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [conceptModalVisible, setConceptModalVisible] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await inventoryAttributeDefinitionService.fetchList();
      if (result.success || result.data) {
        setData(result.data || []);
      } else {
        message.error(t('common.error.load_failed'));
      }
    } catch (error) {
      message.error(t('common.error.network_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    try {
      const result = await inventoryAttributeDefinitionService.delete(id);
      if (result.success) {
        message.success(t('common.message.delete_success'));
        fetchData();
      } else {
        message.error(t('common.error.delete_failed'));
      }
    } catch (error) {
      message.error(t('common.error.network_error'));
    }
  };

  const handleMove = async (record, direction) => {
    const index = data.findIndex(item => item.id === record.id);
    if (index < 0) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= data.length) return;

    const newData = [...data];
    [newData[index], newData[newIndex]] = [newData[newIndex], newData[index]];
    
    setData(newData);
    setLoading(true);

    try {
      // Update positions based on new array index (1-based)
      const itemAtNewIndex = newData[newIndex];
      const itemAtOldIndex = newData[index];

      await inventoryAttributeDefinitionService.update(itemAtNewIndex.id, { position: newIndex + 1 });
      await inventoryAttributeDefinitionService.update(itemAtOldIndex.id, { position: index + 1 });

      message.success(t('common.message.update_success'));
      fetchData();
    } catch (error) {
      message.error(t('common.error.operation_failed'));
      setLoading(false);
      fetchData();
    }
  };

  const columns = useMemo(() => [
    {
      title: t('shared.inventory_attribute_definition.fields.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('shared.inventory_attribute_definition.fields.label'),
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: t('shared.inventory_attribute_definition.fields.data_type'),
      dataIndex: 'data_type',
      key: 'data_type',
      render: (text) => <Tag>{t(`shared.inventory_attribute_definition.data_types.${text}`, text)}</Tag>
    },
    {
      title: t('shared.inventory_attribute_definition.fields.required'),
      dataIndex: 'required',
      key: 'required',
      render: (required) => required ? <Tag color="red">{t('common.yes')}</Tag> : <Tag>{t('common.no')}</Tag>
    },
    {
      title: t('shared.inventory_attribute_definition.fields.position'),
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: t('common.table.actions'),
      key: 'actions',
      render: (_, row, index) => (
        <Space>
          <Button
            icon={<ArrowUpOutlined />}
            size="small"
            disabled={index === 0}
            onClick={() => handleMove(row, 'up')}
          />
          <Button
            icon={<ArrowDownOutlined />}
            size="small"
            disabled={index === data.length - 1}
            onClick={() => handleMove(row, 'down')}
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setEditingRecord(row);
              setIsModalVisible(true);
            }}
          />
          <Popconfirm
            title={t('common.confirm.delete')}
            onConfirm={() => handleDelete(row.id)}
            okText={t('common.button.confirm')}
            cancelText={t('common.button.cancel')}
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      )
    }
  ], [t, data]);

  return (
    <>
    <ListPageHeader
      basePath="/admin"
      items={['data', 'data.inventory_attribute_definitions']}
      guideButton={
        <Tooltip title={t('concepts.inventory_attribute_definitions.tooltip')}>
          <Button
            type="text"
            size="small"
            icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
            onClick={() => setConceptModalVisible(true)}
            style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
          />
        </Tooltip>
      }
      actions={
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={() => {
            setEditingRecord(null);
            setIsModalVisible(true);
          }}
        >
          {t('common.button.new')}
        </Button>
      }
    />
    <Card>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={false}
      />
    </Card>

    <DefinitionForm
      visible={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      onSuccess={() => {
        setIsModalVisible(false);
        fetchData();
      }}
      record={editingRecord}
    />

    <InventoryAttributeDefinitionConceptModal
      visible={conceptModalVisible}
      onClose={() => setConceptModalVisible(false)}
    />
  </>
  );
}
